import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Grid,
  IconButton,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { fetchClipSummary } from "data/queries";
import {
  Copy,
  Download,
  Edit,
  Eye,
  FileText,
  Gem,
  Trash2,
  X,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { resolveIdentityId } from "utils/identity";
import { ClipConfiguration } from "../clip-ui/models";
import { formatTimestamp } from "utils";
import { useRef, useState } from "react";
import { Basemap } from "components/basemap/basemap";
import { FillLayer, Layer, LineLayer, MapRef, Source } from "react-map-gl";
import bbox from "@turf/bbox";
import { SummaryTable } from "../clip-ui/steps/step-4-summary/summary-table";
import { openDownloadLinkCallback } from "utils/download";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { hasPremiumData } from "../clip-ui/clip-menu";
import { formatClipArea, formatCRS } from "../clip-utils";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { PermissionTooltipButton } from "components/permissions/permissions";
import { useHistory } from "react-router-dom";
import * as Routes from "routes";
import { deleteClip } from "data/mutations";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import ConfirmationModal from "components/modals/confirmation-modal";

interface ClipDetailSidebarProps {
  clip: ClipConfiguration;
  onClose: () => void;
}

export const ClipDetailSidebar = ({
  clip,
  onClose,
}: ClipDetailSidebarProps) => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const theme = useTheme();
  const mapRef = useRef<MapRef>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { enqueueInfoSnackbar } = useCustomSnackbars();
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  // Check if clip has premium data to determine which permission to check
  const clipHasPremiumDataForDownload = hasPremiumData(clip);

  // Determine if clip is editable based on status
  const isCompleted = clip.status === "Completed";

  // Delete mutation
  const clipDeleteMutation = useMutation(() => deleteClip(clip.id), {
    onSuccess: async () => {
      await queryClient.invalidateQueries([QueryKeys.clipsKey]);
      setDeleteModalIsOpen(false);
      onClose();
      enqueueInfoSnackbar("Clip deleted successfully", "success");
    },
    onError: (error: Error) => {
      enqueueInfoSnackbar(error.message, "error");
    },
  });

  // Check if clip can be deleted (not Processing or Quoting)
  const canDeleteClip =
    clip.status !== "Processing" && clip.status !== "Quoting";

  const clipSummaryQuery = useQuery(
    [QueryKeys.clipSummaryKey, identityId, clip.id],
    () => fetchClipSummary(clip.id),
    {
      enabled: clip.status !== "Draft",
    }
  );

  const [minLng, minLat, maxLng, maxLat] = clip.geometry
    ? bbox(clip.geometry)
    : [133.7751 - 10, -25.2744 - 10, 133.7751 + 10, -25.2744 + 10]; // Center of Australia with padding

  const fillLayer: FillLayer = {
    id: "clip-fill",
    type: "fill",
    paint: {
      "fill-color": theme.palette.primary.main,
      "fill-opacity": 0.3,
    },
  };

  const lineLayer: LineLayer = {
    id: "clip-outline",
    type: "line",
    paint: {
      "line-color": theme.palette.primary.main,
      "line-width": 2,
    },
  };

  const author = clip.authorName || clip.developer || "";
  const purchaser =
    clip.purchaserName ||
    clip.authorName ||
    (clip.status === "Completed" ? clip.developer : "") ||
    "";
  const isPremiumClip = hasPremiumData(clip);

  const handleCopyClipId = () => {
    navigator.clipboard.writeText(clip.id);
  };

  // Loading state - show skeleton while clip summary is loading
  if (clipSummaryQuery.isLoading) {
    return (
      <Card
        sx={{
          minWidth: 480,
          maxWidth: 600,
          height: "100%",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
        }}
      >
        <CardContent
          sx={{ p: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Skeleton variant="text" width={200} height={40} />
            <IconButton size="small" onClick={onClose}>
              <X size={20} />
            </IconButton>
          </Box>
          <Skeleton variant="text" width={150} />
          <Skeleton variant="rectangular" height={100} />
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={300} />
          <Skeleton variant="rectangular" height={100} />
          <Box display="flex" gap={1}>
            <Skeleton variant="rectangular" height={36} sx={{ flex: 1 }} />
            <Skeleton variant="rectangular" height={36} sx={{ flex: 1 }} />
            <Skeleton variant="rectangular" height={36} sx={{ flex: 1 }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card
      sx={{
        minWidth: 480,
        maxWidth: 600,
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
      }}
    >
      <CardContent
        sx={{ p: 1, display: "flex", flexDirection: "column", gap: 1 }}
      >
        {/* Error Handling */}
        <ReactQueryErrorWrapper queries={[clipSummaryQuery]} />
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Typography variant="h5" fontWeight="bold">
            {clip.name}
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            {isPremiumClip && <Gem color="#1876d3" size={20} />}
            <IconButton size="small" onClick={onClose}>
              <X size={20} />
            </IconButton>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2" color="text.secondary">
            Clip ID: {clip.id}
          </Typography>
          <IconButton size="small" onClick={handleCopyClipId}>
            <Copy size={16} />
          </IconButton>
        </Box>

        {/* Metadata Grid */}
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              Format
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {clip.format || "ESRI Shapefile"}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              Datum
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {formatCRS(clip.crs)}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              Clip Area
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {formatClipArea(clip.geometry)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Client Reference
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {clip.clientReference || "-"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {clip.status}
            </Typography>
          </Grid>
        </Grid>

        {/* Note */}
        {clip.notes && (
          <>
            <Typography variant="subtitle2">Note</Typography>
            <Typography variant="body2" color="text.secondary">
              {clip.notes}
            </Typography>
          </>
        )}

        {/* Data Section */}
        <Typography variant="subtitle2" fontWeight="bold">
          Data
        </Typography>

        {clipSummaryQuery.data ? (
          <SummaryTable
            quoteSummary={clipSummaryQuery.data}
            showFeatures={true}
            showRate={true}
            showCredits={true}
            showTotal={true}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Quote summary not available for draft clips
          </Typography>
        )}

        {/* Map */}
        {clip.geometry && (
          <Box
            sx={{
              width: "100%",
              height: 300,
              borderRadius: 1,
              overflow: "hidden",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Basemap
              key={clip.id}
              // @ts-ignore
              mapRef={mapRef}
              attributionControl={false}
              onZoom={() => {}}
              style={{
                width: "100%",
                height: "100%",
              }}
              interactive={true}
              bbox={[minLng, minLat, maxLng, maxLat]}
              bboxPadding={40}
            >
              <Source type="geojson" data={clip.geometry}>
                <Layer {...fillLayer} />
                <Layer {...lineLayer} />
              </Source>
            </Basemap>
          </Box>
        )}

        {/* Timeline */}
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body2">
              {formatTimestamp(new Date(clip.date).getTime())}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Modified
            </Typography>
            <Typography variant="body2">
              {clip.lastEditedAt
                ? formatTimestamp(clip.lastEditedAt * 1000)
                : formatTimestamp(new Date(clip.date).getTime())}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Purchased
            </Typography>
            <Typography variant="body2">
              {clip.purchasedOn
                ? formatTimestamp(clip.purchasedOn * 1000)
                : clip.status === "Completed"
                ? formatTimestamp(new Date(clip.date).getTime())
                : "-"}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Completed
            </Typography>
            <Typography variant="body2">
              {clip.completedOn
                ? formatTimestamp(clip.completedOn * 1000)
                : clip.status === "Completed"
                ? formatTimestamp(new Date(clip.date).getTime())
                : "-"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Author
            </Typography>
            <Typography variant="body2">{author || "-"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Purchaser
            </Typography>
            <Typography variant="body2">{purchaser || "-"}</Typography>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box display="flex" gap={1}>
          <PermissionTooltipButton
            permissionKeys={[PermissionsKeys.clipDelete]}
            additionalDisabled={!canDeleteClip}
            variant="contained"
            size="small"
            color="error"
            startIcon={<Trash2 size={16} />}
            onClick={() => setDeleteModalIsOpen(true)}
            sx={{ flex: "none", minWidth: "fit-content" }}
          >
            Delete
          </PermissionTooltipButton>
          <ButtonGroup fullWidth variant="contained" size="small">
            <Button
              startIcon={isCompleted ? <Eye size={16} /> : <Edit size={16} />}
              onClick={() => {
                history.push(`${Routes.clipUi}?clip_id=${clip.id}`);
              }}
            >
              {isCompleted ? "View" : "Edit"}
            </Button>
            <Button
              startIcon={<FileText size={16} />}
              disabled={!clip.invoiceId}
              onClick={() => {
                if (clip.invoiceUrl) {
                  window.open(clip.invoiceUrl, "_blank");
                }
              }}
            >
              Invoice
            </Button>
            {clipHasPremiumDataForDownload ? (
              <PermissionTooltipButton
                permissionKeys={[PermissionsKeys.premiumDataRead]}
                additionalDisabled={clip.status !== "Completed"}
                startIcon={<Download size={16} />}
                onClick={openDownloadLinkCallback(
                  `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${
                    clip.id
                  }/output`
                )}
              >
                Download
              </PermissionTooltipButton>
            ) : (
              <Button
                startIcon={<Download size={16} />}
                disabled={clip.status !== "Completed"}
                onClick={openDownloadLinkCallback(
                  `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${
                    clip.id
                  }/output`
                )}
              >
                Download
              </Button>
            )}
          </ButtonGroup>
        </Box>
      </CardContent>
    </Card>

    {/* Delete Confirmation Modal */}
    <ConfirmationModal
      isLoading={clipDeleteMutation.isLoading}
      isError={clipDeleteMutation.isError}
      open={deleteModalIsOpen}
      header="Delete this clip?"
      body="This clip will be permanently removed from your organisation. You won't be able to recover its content"
      leftButtonText="CANCEL"
      leftButtonColor="primary"
      rightButtonText="DELETE CLIP"
      rightButtonColor="error"
      handleLeftButton={() => setDeleteModalIsOpen(false)}
      handleRightButton={() => clipDeleteMutation.mutate()}
      handleClose={() => setDeleteModalIsOpen(false)}
      type="Clip"
    />
  </>
  );
};
